import type { SeriesClickCallbackFunction, TitleOptions } from 'highcharts';
import { useMemo } from 'react';

interface PieChartOptions {
  donut?: boolean;
  onClick?: SeriesClickCallbackFunction;
  showAsPercentage?: boolean;
  label?: {
    total: string;
    description: string;
  };
  /**
   * Formats the aggregated point value for tooltips and data labels (e.g. the
   * 2-dp consequence cost formatter). When omitted, the raw `{point.y}` string
   * formats are retained unchanged. Has no effect when `showAsPercentage` is
   * set — percentage display is left untouched (RSP-5735).
   */
  valueFormatter?: (value: number) => string;
}

/**
 * Builds the pie data-label string (`{name}<br />{formatted}`) for a formatted
 * value. Exported so the `this`-bound Highcharts callback stays thin and is
 * unit-testable without faking a full `Highcharts.Point` context.
 */
export const formatPieDataLabel = (
  pointName: string | undefined,
  value: number,
  valueFormatter: (value: number) => string
): string => `${pointName ?? ''}<br />${valueFormatter(value)}`;

/**
 * Builds the pie tooltip string (`Value: {formatted}<br/> Percentage: {pct}%`)
 * for a formatted summed/averaged value. Exported for the same reason as
 * {@link formatPieDataLabel}.
 *
 * "Value:" is intentionally hardcoded, matching the pre-existing untranslated
 * chart tooltip copy ("Count:", "Percentage:") in this hook — if chart
 * tooltip strings are ever localized, migrate them together.
 */
export const formatPieTooltipPoint = (
  value: number,
  percentage: number | undefined,
  valueFormatter: (value: number) => string
): string =>
  `Value: ${valueFormatter(value)}<br/> Percentage: ${(percentage ?? 0).toFixed(1)}%`;

// Below these dimensions (small in BOTH width and height) there's no room for
// the descriptor under the total in the donut centre, so the centre label
// collapses to just the total. Growing either dimension restores the full
// "total + descriptor".
const DONUT_TOTAL_ONLY_MAX_WIDTH = 350;
const DONUT_TOTAL_ONLY_MAX_HEIGHT = 250;

const DATA_LABEL_RIGHT_SPACING = 60;
const DATA_LABEL_RIGHT_SPACING_MEDIUM = 30;
const DATA_LABEL_RIGHT_SPACING_SMALL = 15;

const getLabelHtml = (
  donut: boolean | undefined,
  label: PieChartOptions['label'],
  // At the smallest sizes there's no room for the descriptor under the total,
  // so render the total on its own.
  totalOnly = false
) => {
  if (donut && label) {
    const descriptionHtml = totalOnly
      ? ''
      : `<br /><span style="font-size: 75%;">${label?.description}</span>`;

    return `
        <div style="text-align: center;">
          <span style="font-weight: 700;">${label?.total}</span>${descriptionHtml}
        </div>`;
  }

  return '';
};

const getTitleOptions = (
  donut: boolean | undefined,
  label: PieChartOptions['label']
): TitleOptions => {
  return {
    verticalAlign: 'middle',
    align: 'center',
    floating: true,
    useHTML: true,
    text: getLabelHtml(donut, label),
  };
};

export const useGetPieChartDefaultOptions = ({
  donut,
  onClick,
  showAsPercentage,
  label,
  valueFormatter,
}: PieChartOptions): Highcharts.Options => {
  return useMemo(() => {
    // Only format value labels for summed/averaged values; percentage display
    // is left untouched. Capturing the narrowed function keeps the formatter
    // callbacks free of non-null assertions.
    const valueLabelFormatter = showAsPercentage ? undefined : valueFormatter;
    const options: Highcharts.Options = {
      chart: {
        type: 'pie',
        spacingRight: DATA_LABEL_RIGHT_SPACING,
        events: {
          render: function () {
            if (this.plotWidth) {
              const rootFontSize = parseFloat(
                getComputedStyle(document.documentElement).fontSize
              );
              const fontSizePx = Math.max(
                0.625 * rootFontSize, // 0.425rem min
                Math.min(1.2 * rootFontSize, this.chartWidth * 0.045) // 1.2rem max
              );
              const fontSizeRem = fontSizePx / rootFontSize;

              // Collapse to just the total when the donut is small in BOTH
              // dimensions (no room for the descriptor under it).
              const totalOnly =
                this.chartWidth <= DONUT_TOTAL_ONLY_MAX_WIDTH &&
                this.chartHeight <= DONUT_TOTAL_ONLY_MAX_HEIGHT;

              this.setTitle({
                text: getLabelHtml(donut, label, totalOnly),
                style: { fontSize: fontSizeRem + 'rem' },
              });

              // Centre the donut centre-label exactly on the pie centre. Title
              // x/y options don't reliably move a floating useHTML title, and
              // verticalAlign:middle anchors 1-line vs 2-line labels
              // differently — so reset any prior nudge, measure the laid-out
              // label, then translate it so its bounding-box centre sits on the
              // plot centre (= pie centre; the pie uses center:['50%','50%']).
              const centreLabel = this.title;
              if (centreLabel) {
                centreLabel.translate(0, 0);
                const bbox = centreLabel.getBBox(true);
                centreLabel.translate(
                  Math.round(
                    this.plotLeft +
                      this.plotWidth / 2 -
                      (bbox.x + bbox.width / 2)
                  ),
                  Math.round(
                    this.plotTop +
                      this.plotHeight / 2 -
                      (bbox.y + bbox.height / 2)
                  )
                );
              }
            }
          },
        },
      },
      exporting: {
        chartOptions: {
          title: getTitleOptions(donut, label),
        },
        filename: 'chart',
      },
      title: getTitleOptions(donut, label),
      plotOptions: {
        pie: {
          size: '75%',
          innerSize: donut ? '75%' : '0',
          center: ['50%', '50%'], // force chart to be centered to ensure label is positioned correctly
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            ...(valueLabelFormatter
              ? {
                  formatter: function pieDataLabelFormatter(
                    this: Highcharts.Point
                  ) {
                    return formatPieDataLabel(
                      this.name,
                      this.y ?? 0,
                      valueLabelFormatter
                    );
                  },
                }
              : {
                  format: `{point.name}<br />${showAsPercentage ? '{point.percentage:.0f}%' : '{point.y}'}`,
                }),
            crop: false,
            overflow: 'allow',
            style: {
              textOverflow: 'none',
              textAlign: 'left',
            },
          },
          events: {
            click: onClick,
          },
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 510,
            },
            chartOptions: {
              chart: {
                spacingRight: DATA_LABEL_RIGHT_SPACING_MEDIUM,
              },
              plotOptions: {
                pie: {
                  size: '60%',
                  dataLabels: {
                    enabled: true,
                    style: {
                      fontSize: '10px',
                    },
                    distance: 5,
                  },
                },
              },
            },
          },
          {
            condition: {
              maxWidth: 350,
            },
            chartOptions: {
              chart: {
                spacingRight: DATA_LABEL_RIGHT_SPACING_SMALL,
              },
              plotOptions: {
                pie: {
                  size: '50%',
                  dataLabels: {
                    enabled: true,
                    style: {
                      fontSize: '8px',
                    },
                    distance: 3,
                  },
                },
              },
            },
          },
        ],
      },
      tooltip: {
        headerFormat: '<b>{point.name}</b><br/>',
        ...(valueLabelFormatter
          ? {
              pointFormatter: function pieTooltipPointFormatter(
                this: Highcharts.Point
              ) {
                return formatPieTooltipPoint(
                  this.y ?? 0,
                  this.percentage,
                  valueLabelFormatter
                );
              },
            }
          : {
              pointFormat:
                'Count: {point.y}<br/> Percentage: {point.percentage:.1f}%',
            }),
        outside: true,
      },
    };

    return options;
  }, [label, donut, onClick, showAsPercentage, valueFormatter]);
};
